import React, { useCallback, useEffect, useState } from 'react';
import CustomModal from '../CustomModal';
import { Col, DatePicker, Flex, Form, Input, Row, Select, Typography } from 'antd';
import { getUsers } from '../../redux/actions/users';
import { getFullName } from '../../utils';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { addAlternateUser, updateAlternateUsers } from '../../redux/actions/alternate-user';

const { Option } = Select;

const SecondaryUserModal = ({ open, onCancel, data }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const handleSearch = useCallback(
        debounce(async (value) => {
            setLoading(true);
            const res = await getUsers({ searchText: value, loading: false, isDispatch: false });
            setLoading(false);
            if (res?.ack) setUsers(res.users);
        }, 300),
        []
    );

    const handleFinish = async (values) => {
        setIsSubmitting(true);
        const formattedValues = {
            ...values,
            start_date: values.start_date ? dayjs(values.start_date).format('YYYY-MM-DD HH:mm:ss') : null,
            end_date: values.end_date ? dayjs(values.end_date).format('YYYY-MM-DD HH:mm:ss') : null,
        };

        const res = data
            ? await updateAlternateUsers({ ...formattedValues, id: data.id })
            : await addAlternateUser(formattedValues);
        if (res?.ack) onCancel?.();
        setIsSubmitting(false);
    };

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                primary_user_id: data?.primary_user?.id,
                secondary_user_id: data?.secondary_user?.id,
                start_date: data?.start_date ? dayjs(data.start_date) : null,
                end_date: data?.end_date ? dayjs(data.end_date) : null,
                notes: data?.notes,
            });
        }
    }, [data, form]);

    useEffect(() => {
        getUsers().then((res) => res?.ack && setUsers(res.users));
    }, []);

    return (
        <CustomModal
            title="Assign Secondary User"
            open={open}
            onCancel={onCancel}
            okText="Submit"
            onOk={() => form.submit()}
            width={600}
            okButtonProps={{ loading: isSubmitting, disabled: !isFormValid }}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                size="large"
                onFieldsChange={(_, allFields) => {
                    const hasErrors = allFields.some((field) => field.errors.length);
                    const hasEmptyRequiredFields = allFields.some(
                        (field) =>
                            field.required &&
                            (field.value === undefined || field.value === null || field.value === '')
                    );
                    setIsFormValid(!hasErrors && !hasEmptyRequiredFields);
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="primary_user_id"
                            label="Primary User"
                            rules={[{ required: true, message: 'Please select the primary user' }]}
                        >
                            <Select
                                placeholder="Primary User"
                                optionLabelProp="label"
                                loading={loading}
                                showSearch
                                allowClear
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                                onDropdownVisibleChange={(open) => {
                                    if (open && !users.length) getUsers({ loading: false });
                                }}
                                onSearch={handleSearch}
                            >
                                {users.map((user) => (
                                    <Option key={user?.id} value={user?.id} label={getFullName(user)}>
                                        <Flex vertical gap={1}>
                                            <Typography.Text>{getFullName(user)}</Typography.Text>
                                            <Typography.Text type="secondary">{user.email}</Typography.Text>
                                        </Flex>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="secondary_user_id"
                            label="Secondary User"
                            rules={[
                                { required: true, message: 'Please select an secondary user' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('primary_user_id') !== value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Both User cannot be the same'));
                                    },
                                }),
                            ]}
                        >
                            <Select
                                placeholder="Secondary User"
                                optionLabelProp="label"
                                loading={loading}
                                showSearch
                                allowClear
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                                onDropdownVisibleChange={(open) => {
                                    if (open && !users.length) getUsers({ loading: false });
                                }}
                                onSearch={handleSearch}
                            >
                                {users.map((user) => (
                                    <Option key={user?.id} value={user?.id} label={getFullName(user)}>
                                        <Flex vertical gap={1}>
                                            <Typography.Text>{getFullName(user)}</Typography.Text>
                                            <Typography.Text type="secondary">{user.email}</Typography.Text>
                                        </Flex>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="start_date"
                            label="Start Date"
                            rules={[{ required: true, message: 'Please select the start date' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Start Date"
                                showTime
                                format="DD-MM-YYYY HH:mm"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="end_date"
                            label="End Date"
                            dependencies={['start_date']}
                            rules={[
                                { required: true, message: 'Please select the end date' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const start = getFieldValue('start_date');
                                        if (!value || !start) {
                                            return Promise.resolve();
                                        }
                                        if (dayjs(value).isAfter(dayjs(start))) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error('End date must be later than Start date')
                                        );
                                    },
                                }),
                            ]}>
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="End Date"
                                showTime
                                format="DD-MM-YYYY HH:mm"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea placeholder="Notes" autoSize={{ minRows: 3, maxRows: 6 }} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </CustomModal>
    );
};

export default SecondaryUserModal;
